import { Flex, Provider } from '@eleveo/components-react';
import { CircleIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { parseAbsoluteToLocal } from '@internationalized/date';
import { AxisLabelsFormatterContextObject, TooltipFormatterContextObject } from 'highcharts';
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { CHART_TOOLTIP_SERIES_TAG } from './constants';
import { IntradaySupportedSeries, SeriesConfig } from './types';
import { DateFormatter } from '../../components/DateFormatter';
import { IntradayPerQueue } from '../../graphql/components';
import { ChartData, RecordBooleanType } from '../../types';

import type { DateFormatterOptions } from 'react-aria';

export const generateChartMetadata = (intradayData?: IntradayPerQueue | null) => {
  const { aht, contactVolume, sla, staffingRequirement } = intradayData?.charts ?? {};

  return {
    aht: [
      {
        id: 'forecast-accuracy',
        nameKey: 'forecastAccuracy',
        value: aht?.info.forecastAccuracy ?? null,
      },
      {
        id: 'last-hour-average-difference',
        nameKey: 'lastHourAverageDifference',
        value: aht?.info.lastHourAverageDifference ?? null,
        showChangeOfTrend: true,
      },
    ],
    contactVolume: [
      {
        id: 'forecast-accuracy',
        nameKey: 'forecastAccuracy',
        value: contactVolume?.info.forecastAccuracy ?? null,
      },
      {
        id: 'last-hour-average-difference',
        nameKey: 'lastHourAverageDifference',
        value: contactVolume?.info.lastHourAverageDifference ?? null,
        showChangeOfTrend: true,
      },
    ],
    sla: [
      {
        id: 'average-sla',
        nameKey: 'averageSla',
        value: sla?.info.averageSla ?? null,
      },
    ],
    staffingRequirement: [
      {
        id: 'forecast-accuracy',
        nameKey: 'forecastAccuracy',
        value: staffingRequirement?.info.forecastAccuracy ?? null,
      },
      {
        id: 'last-hour-average-difference',
        nameKey: 'lastHourAverageDifference',
        value: staffingRequirement?.info.lastHourAverageDifference ?? null,
        showChangeOfTrend: true,
      },
    ],
  };
};

export const determineSeriesEnabledState = <T extends IntradaySupportedSeries>(
  seriesConfig?: SeriesConfig<T>[],
  data?: T
) => {
  if (!seriesConfig || !data) {
    return {};
  }

  return seriesConfig.reduce((state, current: SeriesConfig<typeof data>) => {
    const seriesName = current.nameKey;

    return {
      ...state,
      [seriesName]: !!(data[seriesName] as ChartData[]).length,
    };
  }, {} as RecordBooleanType);
};

export const TOOLTIP_DISTANCE_FROM_POINT = 20;
export const TOOLTIP_DISTANCE_FROM_WINDOW_BORDER = 30;

export function chartXAxisLabelFormatter(locale: string, dateFormatterOptions: DateFormatterOptions) {
  return function formatter(this: AxisLabelsFormatterContextObject) {
    const { value } = this;
    const datetime = parseAbsoluteToLocal(new Date(value).toISOString());

    return renderToStaticMarkup(
      <Provider locale={locale}>
        <DateFormatter className="font-bold" options={dateFormatterOptions}>
          {datetime.toDate()}
        </DateFormatter>
      </Provider>
    );
  };
}

export const chartTooltipFormatter = (locale: string, timezone: string, options?: Intl.NumberFormatOptions) =>
  function formatter(this: TooltipFormatterContextObject) {
    const points = this.points?.filter(({ point }) => !(point as any).hideTooltip) ?? [];

    if (!points.length) {
      return false;
    }

    // Calculation of tooltip's arrow position when tooltip is at the edge of the viewport
    const {
      plotX,
      series: { chart },
    } = points[0].point as Highcharts.Point & Highcharts.TooltipPositionerPointObject;
    const { container, plotLeft, tooltip } = chart;

    const pointClientLeft = container.getBoundingClientRect().left + plotLeft + plotX;
    const tooltipWidth = (tooltip as any).label?.width ?? 0;

    const excessWidth = pointClientLeft + tooltipWidth / 2 + TOOLTIP_DISTANCE_FROM_WINDOW_BORDER - window.innerWidth;
    const arrowX = tooltipWidth / 2 + Math.max(0, excessWidth);

    const datetime = parseAbsoluteToLocal(new Date(this.x).toISOString());

    return renderToStaticMarkup(
      <div className="tooltip font-sans opacity-100">
        <Provider locale={locale}>
          <div className="text-center">
            <DateFormatter
              className="font-bold"
              options={{
                weekday: 'long',
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZone: timezone,
              }}
            >
              {datetime.toDate()}
            </DateFormatter>
          </div>
          {points.map(({ point }) => (
            <Flex
              justifyContent="between"
              alignItems="center"
              gap={10}
              testId={`${CHART_TOOLTIP_SERIES_TAG}:${(point.series as any).userOptions.id}`}
            >
              <Flex alignItems="center" className="gap-5">
                <CircleIcon style={{ color: point.color as string }} />
                {point.series.name}
              </Flex>
              {point.y && (
                <strong>
                  {point.y.toLocaleString(locale, {
                    ...options,
                    maximumFractionDigits: 3,
                  })}
                </strong>
              )}
            </Flex>
          ))}
          <div
            className="tooltip__arrow tooltip__arrow--top"
            style={{
              left: `${arrowX}px`,
            }}
          />
        </Provider>
      </div>
    );
  };
